import {
  HTMLAttributes,
  KeyboardEvent,
  useEffect,
  useRef,
  useState
} from 'react'
import Panzoom, { PanzoomObject } from '@panzoom/panzoom'

import Container from '@/components/base/Container'
import Image from '@/components/base/Image'
import Modal from '@/components/base/Modal'

import { cn } from '@/lib/utils'
import { WPImage, WPPageData } from '@/types'

export type FloorplanProps = HTMLAttributes<HTMLElement> &
  Partial<{
    item: WPImage
    zoom: WPImage
    theme: WPPageData['acf']['theme']
  }>

const FloorplanViewer = ({
  image,
  onClose
}: {
  image: WPImage
  onClose: () => void
}) => {
  const viewport = useRef<HTMLDivElement>(null)
  const canvas = useRef<HTMLDivElement>(null)
  const panzoom = useRef<PanzoomObject | null>(null)
  const [scale, setScale] = useState(1)

  useEffect(() => {
    const element = canvas.current
    const parent = viewport.current
    if (!element || !parent) return

    const instance = Panzoom(element, {
      contain: 'outside',
      minScale: 1,
      maxScale: 5,
      step: 0.25,
      panOnlyWhenZoomed: true
    })
    panzoom.current = instance

    const updateScale = () => setScale(instance.getScale())
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
      if (event.key !== 'Tab') return

      const buttons = Array.from(
        parent.parentElement?.querySelectorAll<HTMLButtonElement>('button') ||
          []
      )
      const first = parent
      const last = buttons[buttons.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last?.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first?.focus()
      }
    }

    element.addEventListener('panzoomchange', updateScale)
    parent.addEventListener('wheel', instance.zoomWithWheel, { passive: false })
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      parent.removeEventListener('wheel', instance.zoomWithWheel)
      element.removeEventListener('panzoomchange', updateScale)
      instance.destroy()
      panzoom.current = null
    }
  }, [onClose])

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const delta: Record<string, [number, number]> = {
      ArrowLeft: [40, 0],
      ArrowRight: [-40, 0],
      ArrowUp: [0, 40],
      ArrowDown: [0, -40]
    }
    if (scale > 1 && delta[event.key]) {
      event.preventDefault()
      panzoom.current?.pan(...delta[event.key], { relative: true })
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Planimetria ingrandita"
      className="pointer-events-auto fixed inset-0 z-modal bg-dark-900 text-white"
    >
      <div
        ref={viewport}
        className="absolute inset-0 overflow-hidden"
        tabIndex={0}
        aria-label="Usa le frecce per spostare la planimetria ingrandita"
        onKeyDown={handleKeyDown}
      >
        <div ref={canvas} className="relative h-full w-full">
          <Image
            source={image}
            fill
            unoptimized
            fit="contain"
            alt={image.alt || 'Planimetria'}
            className="pointer-events-none select-none"
            draggable={false}
          />
        </div>
      </div>
      <button
        type="button"
        autoFocus
        aria-label="Chiudi planimetria"
        onClick={onClose}
        className="absolute right-gutter top-gutter z-10 flex size-[4.4rem] items-center justify-center bg-dark-500 text-[2.4rem]"
      >
        ×
      </button>
      <div className="absolute bottom-gutter left-1/2 z-10 flex -translate-x-1/2 gap-[0.4rem] bg-dark-500 p-[0.4rem]">
        <button
          type="button"
          aria-label="Riduci zoom"
          disabled={scale <= 1}
          onClick={() => panzoom.current?.zoomOut()}
          className="size-[4.4rem] text-[2.4rem] disabled:opacity-40"
        >
          −
        </button>
        <button
          type="button"
          aria-label="Ripristina zoom"
          onClick={() => panzoom.current?.reset()}
          className="min-w-[4.4rem] px-[0.8rem] text-small"
        >
          {Math.round(scale * 100)}%
        </button>
        <button
          type="button"
          aria-label="Aumenta zoom"
          disabled={scale >= 5}
          onClick={() => panzoom.current?.zoomIn()}
          className="size-[4.4rem] text-[2.4rem] disabled:opacity-40"
        >
          +
        </button>
      </div>
    </div>
  )
}

const Floorplan = ({ item, zoom, theme, className }: FloorplanProps) => {
  const [open, setOpen] = useState(false)
  const trigger = useRef<HTMLButtonElement>(null)

  if (!item?.url) return null

  return (
    <>
      <section
        className={cn(theme === 'dark' ? 'bg-dark-600' : 'bg-white', className)}
      >
        <Container>
          <button
            ref={trigger}
            type="button"
            aria-label="Apri planimetria ingrandita"
            onClick={() => setOpen(true)}
            className="group relative mx-auto block aspect-[3/2] w-full max-w-[114.4rem] overflow-hidden bg-dark-200"
          >
            <Image
              source={item}
              fill
              fit="contain"
              sizes="(min-width: 1200px) 1144px, 100vw"
              alt={item.alt || 'Planimetria'}
            />
            <span className="absolute bottom-gutter right-gutter bg-dark-500 px-[1.6rem] py-[1.2rem] text-small font-extrabold uppercase text-white">
              Ingrandisci +
            </span>
          </button>
        </Container>
      </section>
      <Modal
        open={open}
        onClosed={() => trigger.current?.focus({ preventScroll: true })}
      >
        <FloorplanViewer
          image={zoom?.url ? zoom : item}
          onClose={() => setOpen(false)}
        />
      </Modal>
    </>
  )
}

export default Floorplan
